import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { FileStack } from 'lucide-react';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import type { IntakeDraft } from '../../services/intakeDrafts';

/**
 * Draft referrals, in a table of their own.
 *
 * Not rows in the intakes table. A draft has no patient, status, clinic,
 * insurance, referring provider or SOC date, because none of those exist until
 * an intake has run, so it would sit in that table as a row of empty cells
 * reading as missing data. These are the four things a draft does have.
 */

export interface DraftIntakeTableProps {
  drafts: IntakeDraft[];
  loading?: boolean;
  onOpen: (draft: IntakeDraft) => void;
  /** Shown in the empty state, so a fruitless search says why. */
  searching?: boolean;
}

export default function DraftIntakeTable({
  drafts,
  loading = false,
  onOpen,
  searching = false,
}: DraftIntakeTableProps) {
  return (
    <div
      className={
        loading
          ? 'rounded-md border opacity-60 transition-opacity'
          : 'rounded-md border transition-opacity'
      }
      aria-busy={loading}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Draft</TableHead>
            <TableHead>Documents</TableHead>
            <TableHead>Started by</TableHead>
            <TableHead>Started</TableHead>
            <TableHead className='w-20' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {drafts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className='py-10 text-center'>
                <FileStack className='mx-auto mb-2 h-6 w-6 text-muted-foreground' />
                <p className='font-medium'>
                  {searching
                    ? 'No drafts match that search'
                    : 'No draft intakes'}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {searching
                    ? 'Drafts are found by the name somebody started them under.'
                    : 'Start one from the Fax Inbox, when a referral arrives for a patient you do not have yet.'}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            drafts.map(draft => (
              <TableRow
                key={draft.id}
                className='cursor-pointer'
                onClick={() => onOpen(draft)}
              >
                <TableCell className='font-medium'>{draft.name}</TableCell>
                <TableCell>
                  {draft.document_count === 1
                    ? '1 document'
                    : `${draft.document_count} documents`}
                </TableCell>
                <TableCell>{draft.created_by_name ?? '-'}</TableCell>
                <TableCell>{formatDateToMMDDYYYY(draft.created_at)}</TableCell>
                <TableCell className='text-right text-sm text-muted-foreground'>
                  Open
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
